import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CalendarPlus, CheckCircle2, HeartHandshake, Play, ShieldCheck } from "lucide-react";

import { disciplineService, type InterventionAction, type InterventionPlan, type StaffOption, type StudentOption } from "../../services/disciplineService";
import { apiErrorMessage } from "../../utils/apiError";
import { daysFromTodayIso, nowLocalInput, todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { SearchPick } from "../library/Pickers";
import { StaffPicker } from "../register/StaffPicker";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { humanise } from "../register/registerLogic";
import { PLAN_TYPES, RATINGS, SESSION_TYPES, day, isOpenSession, labelFrom, planConfig, planStatus, sessionStatus, when } from "./disciplineLogic";

const lines = (text: string) => text.split("\n").map((line) => line.trim()).filter(Boolean);

/** A new learner support plan; saved as a draft. */
function PlanForm({ learners, staff, me, onClose, onDone }: { learners: StudentOption[]; staff: StaffOption[]; me?: number; onClose: () => void; onDone: (row: InterventionPlan) => void }) {
  const [learner, setLearner] = useState("");
  const [kind, setKind] = useState("supportive");
  const [start, setStart] = useState(todayIso());
  const [goal, setGoal] = useState("");
  const [description, setDescription] = useState("");
  const [team, setTeam] = useState<number[]>(me ? [me] : []);
  const save = useMutation({ mutationFn: disciplineService.createInterventionPlan, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim();
    save.mutate({
      student: Number(learner),
      title: text("title"),
      plan_type: kind as InterventionPlan["plan_type"],
      start_date: start,
      end_date: text("end_date"),
      review_frequency_days: Number(text("review_frequency_days")) || 14,
      primary_goal: goal.trim(),
      description: description.trim(),
      specific_objectives: lines(text("specific_objectives")),
      success_metrics: lines(text("success_metrics")),
      responsible_staff: team,
      parent_involvement: data.get("parent_involvement") === "on",
    });
  };
  const sorted = [...learners].sort((a, b) => (a.class_level_order ?? 999) - (b.class_level_order ?? 999) || a.full_name.localeCompare(b.full_name));
  return (
    <FormModal
      id="support-plan"
      title="New support plan"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The plan could not be created.") : undefined}
      saveLabel="Save as a draft"
      note="The committee starts it, once the parent has agreed where needed."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The plan" divided={false}>
        <Field label="Learner" as="div" className="sm:col-span-2">
          <SearchPick required items={sorted} value={learner} onChange={setLearner} label={(row) => row.full_name} detail={(row) => [row.class_name, row.student_id].filter(Boolean).join(" · ")} placeholder="Search by name, class or learner number" empty="No learner matches" />
        </Field>
        <Field label="Name" hint="e.g. Settling-in support, Term 3 conduct plan.">
          <input required name="title" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Kind of plan">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="input mt-1">
            {PLAN_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Main goal" as="div" className="sm:col-span-2" hint="One positive sentence about what the learner will do.">
          <AIAssistField rows={2} value={goal} onChange={(next) => setGoal(next)} placeholder="e.g. Resolve disagreements by talking or asking a teacher, without pushing or shouting." context={{ key: "discipline.note", params: { kind: "plan_goal", plan_type: kind } }} draftLabel="Word it" draftHint="Jot the aim first; it words it positively." />
        </Field>
        <Field label="What the support involves" as="div" className="sm:col-span-2">
          <AIAssistField rows={3} value={description} onChange={(next) => setDescription(next)} placeholder="e.g. Weekly mentoring with the class teacher, a daily check-in card and a parent meeting at mid-term." context={{ key: "discipline.note", params: { kind: "plan", plan_type: kind } }} draftLabel="Write it up" draftHint="Jot the parts of the plan first; it adds nothing you did not say." />
        </Field>
      </FormSection>
      <FormSection title="Measures">
        <Field label="Objectives" optional className="sm:col-span-2" hint="One per line.">
          <textarea name="specific_objectives" rows={3} className="input mt-1" placeholder={"Attends every mentoring session\nUses the calm-down corner when upset"} />
        </Field>
        <Field label="How success is judged" optional className="sm:col-span-2" hint="One per line.">
          <textarea name="success_metrics" rows={2} className="input mt-1" placeholder="No repeat incident for four weeks" />
        </Field>
      </FormSection>
      <FormSection title="Timing and people">
        <Field label="Starts">
          <input required type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input mt-1" />
        </Field>
        <Field label="Ends">
          <input required type="date" name="end_date" min={start} defaultValue={daysFromTodayIso(42)} className="input mt-1" />
        </Field>
        <Field label="Review every" hint="Days between progress reviews.">
          <input required type="number" min={1} name="review_frequency_days" defaultValue={14} className="input mt-1" />
        </Field>
        <label className="flex items-start gap-2 self-end pb-2 text-sm text-gray-700">
          <input type="checkbox" name="parent_involvement" defaultChecked className="mt-0.5 h-4 w-4 rounded border-gray-300" />
          <span>The parent is involved <span className="block text-xs text-gray-500">Their consent is recorded before the plan starts.</span></span>
        </label>
        <Field label="Staff responsible" as="div" className="sm:col-span-2">
          <StaffPicker staff={staff} value={team} onChange={setTeam} />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** A support session within a plan: mentoring, a parent meeting, counselling. */
function SessionForm({ plan, staff, me, onClose, onDone }: { plan: InterventionPlan; staff: StaffOption[]; me?: number; onClose: () => void; onDone: () => void }) {
  const [kind, setKind] = useState("mentoring");
  const [person, setPerson] = useState(me ? String(me) : "");
  const [description, setDescription] = useState("");
  const save = useMutation({ mutationFn: disciplineService.createInterventionAction, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({
      intervention_plan: plan.id,
      action_type: kind,
      title: String(data.get("title") ?? "").trim(),
      description: description.trim(),
      responsible_staff: person ? Number(person) : undefined,
      scheduled_date: new Date(String(data.get("scheduled_date"))).toISOString(),
      duration_minutes: Number(data.get("duration_minutes")) || 30,
    });
  };
  return (
    <FormModal id="support-session" title={`Plan a session: ${plan.student_name}`} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The session could not be planned.") : undefined} saveLabel="Plan it" onClose={onClose} onSubmit={submit}>
      <FormSection title="The session" divided={false}>
        <Field label="Kind">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="input mt-1">
            {SESSION_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Title" hint="e.g. Weekly check-in, Meeting with Mr Okello.">
          <input required name="title" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="When">
          <input required type="datetime-local" name="scheduled_date" min={nowLocalInput()} defaultValue={nowLocalInput()} className="input mt-1" />
        </Field>
        <Field label="Minutes">
          <input required type="number" min={1} max={480} name="duration_minutes" defaultValue={30} className="input mt-1" />
        </Field>
        <Field label="Led by" as="div" className="sm:col-span-2">
          <SearchPick required items={staff} value={person} onChange={setPerson} label={(row) => row.full_name} detail={(row) => humanise(row.role)} placeholder="Search staff" empty="No one matches" />
        </Field>
        <Field label="What it covers" as="div" className="sm:col-span-2">
          <AIAssistField rows={2} value={description} onChange={(next) => setDescription(next)} placeholder="e.g. Go over the week's check-in card and practise the calm-down steps." context={{ key: "discipline.note", params: { kind: "action", id: plan.id } }} draftLabel="Write it up" draftHint="Jot the points first; it adds nothing you did not say." />
        </Field>
      </FormSection>
    </FormModal>
  );
}

type SessionStep = { session: InterventionAction; step: "done" | "move" | "cancel" };

/** Recording how a session went, moving it or calling it off. */
function SessionStepForm({ step, onClose, onDone }: { step: SessionStep; onClose: () => void; onDone: (text: string) => void }) {
  const { session } = step;
  const [notes, setNotes] = useState("");
  const [rating, setRating] = useState("");
  const [moveTo, setMoveTo] = useState(nowLocalInput());
  const [followUp, setFollowUp] = useState("");
  const save = useMutation({
    mutationFn: () =>
      step.step === "done"
        ? disciplineService.completeInterventionAction({ id: session.id, data: { outcome_notes: notes.trim(), effectiveness_rating: Number(rating), follow_up_required: Boolean(followUp), follow_up_date: followUp ? new Date(followUp).toISOString() : undefined } })
        : step.step === "move"
          ? disciplineService.rescheduleInterventionAction({ id: session.id, data: { reason: notes.trim(), scheduled_date: new Date(moveTo).toISOString() } })
          : disciplineService.cancelInterventionAction({ id: session.id, reason: notes.trim() }),
    onSuccess: () => onDone(step.step === "done" ? `${session.title} is done.` : step.step === "move" ? `${session.title} was moved.` : `${session.title} was cancelled.`),
  });
  const titles = { done: `How did it go? ${session.title}`, move: `Move ${session.title}`, cancel: `Cancel ${session.title}` };
  const kinds = { done: "action_outcome", move: "reschedule", cancel: "action_cancel" };
  return (
    <FormModal
      id="session-step"
      title={titles[step.step]}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "That could not be saved.") : undefined}
      saveLabel={step.step === "done" ? "Mark it done" : step.step === "move" ? "Move it" : "Cancel the session"}
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <FormSection title={step.step === "done" ? "The outcome" : "Why"} divided={false}>
        {step.step === "move" && (
          <Field label="New time">
            <input required type="datetime-local" min={nowLocalInput()} value={moveTo} onChange={(e) => setMoveTo(e.target.value)} className="input mt-1" />
          </Field>
        )}
        {step.step === "done" && (
          <Field label="How much it helped">
            <select required value={rating} onChange={(e) => setRating(e.target.value)} className="input mt-1">
              <option value="">Choose</option>
              {[["5", "Very much"], ["4", "A good deal"], ["3", "Somewhat"], ["2", "A little"], ["1", "Not at all"]].map(([value, label]) => <option key={value} value={value}>{value} · {label}</option>)}
            </select>
          </Field>
        )}
        <Field label={step.step === "done" ? "What happened" : "Reason"} as="div" className="sm:col-span-2">
          <AIAssistField rows={3} value={notes} onChange={(next) => setNotes(next)} placeholder={step.step === "done" ? "e.g. We went through the card; two good days, one hard one. Agreed to practise the breathing steps." : "e.g. The learner was away sick."} context={{ key: "discipline.note", params: { kind: kinds[step.step], id: session.id } }} draftLabel="Write it up" draftHint="Jot the facts first; it adds nothing you did not say." />
        </Field>
        {step.step === "done" && (
          <Field label="Follow up on" optional hint="Leave empty when nothing needs following up.">
            <input type="datetime-local" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="input mt-1" />
          </Field>
        )}
      </FormSection>
    </FormModal>
  );
}

/** A progress review and, at the end, the plan's outcome. */
function ReviewPanel({ plan, finishing, busy, error, onSubmit }: { plan: InterventionPlan; finishing: boolean; busy: boolean; error: unknown; onSubmit: (data: Record<string, unknown>) => void }) {
  const [notes, setNotes] = useState("");
  const [score, setScore] = useState("");
  const [next, setNext] = useState(daysFromTodayIso(plan.review_frequency_days));
  const [lessons, setLessons] = useState("");
  const openSessions = plan.actions.filter(isOpenSession).length;
  return (
    <ActionPanel title={finishing ? "Finish the plan" : "Record a progress review"} description={finishing ? "Say how it ended. Every session must be done or cancelled first." : `Reviews are due every ${plan.review_frequency_days} days.`}>
      {finishing && openSessions > 0 && <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">{openSessions} session{openSessions === 1 ? " is" : "s are"} still open.</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        {finishing ? (
          <Field label="Goal met (%)" hint="Your judgement, 0 to 100.">
            <input type="number" min={0} max={100} value={score} onChange={(e) => setScore(e.target.value)} className="input mt-1" />
          </Field>
        ) : (
          <Field label="Progress">
            <select value={score} onChange={(e) => setScore(e.target.value)} className="input mt-1">
              <option value="">Choose</option>
              {RATINGS.map(([value, label]) => <option key={value} value={value}>{value} · {label}</option>)}
            </select>
          </Field>
        )}
        {!finishing && (
          <Field label="Next review">
            <input type="date" min={todayIso()} value={next} onChange={(e) => setNext(e.target.value)} className="input mt-1" />
          </Field>
        )}
        <Field label={finishing ? "Outcome" : "Progress notes"} as="div" className="sm:col-span-2">
          <AIAssistField rows={3} value={notes} onChange={(next) => setNotes(next)} placeholder={finishing ? "e.g. No repeat incident in five weeks; the learner now asks the teacher for help." : "e.g. Two calm weeks; one argument in the dining hall, handled by walking away."} context={{ key: "discipline.note", params: { kind: finishing ? "outcome" : "review", id: plan.id } }} draftLabel="Write it up" draftHint="Jot what has changed first; it adds nothing you did not say." />
        </Field>
        {finishing && (
          <Field label="Lessons for next time" optional className="sm:col-span-2">
            <textarea rows={2} value={lessons} onChange={(e) => setLessons(e.target.value)} className="input mt-1" />
          </Field>
        )}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={busy || notes.trim().length < 5 || score === "" || (finishing && openSessions > 0)}
          onClick={() => onSubmit(finishing ? { outcome_summary: notes.trim(), success_rate: Number(score), lessons_learned: lessons.trim() } : { progress_notes: notes.trim(), progress_rating: Number(score), next_review_date: next || undefined })}
          className="btn btn-primary gap-1.5"
        >
          <CheckCircle2 className="h-4 w-4" /> {busy ? "Saving…" : finishing ? "Finish the plan" : "Record the review"}
        </button>
      </div>
      {Boolean(error) && errorLine(error, "That could not be saved.")}
    </ActionPanel>
  );
}

/** Learner support plans: consent, sessions, reviews and the outcome. */
export function PlansPanel({ plans, learners, staff, isLoading, isError, onRetry, me, isReviewer, onChanged, initialView }: {
  plans?: InterventionPlan[];
  learners: StudentOption[];
  staff: StaffOption[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  me?: number;
  isReviewer: boolean;
  onChanged: () => void;
  initialView?: string;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [step, setStep] = useState<SessionStep | null>(null);
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    onChanged();
  };
  const consent = useMutation({ mutationFn: disciplineService.recordParentConsent, onSuccess: () => done("The parent's consent is recorded.") });
  const activate = useMutation({ mutationFn: disciplineService.activateIntervention, onSuccess: () => done("The plan has started. Its first review is scheduled.") });
  const review = useMutation({ mutationFn: disciplineService.reviewIntervention, onSuccess: () => done("The review is recorded.") });
  const complete = useMutation({ mutationFn: disciplineService.completeIntervention, onSuccess: () => { setFinishing(false); done("The plan is finished."); } });
  const pause = useMutation({ mutationFn: disciplineService.suspendIntervention, onSuccess: () => done("The plan is paused.") });
  const resume = useMutation({ mutationFn: disciplineService.resumeIntervention, onSuccess: () => done("The plan is running again.") });
  const cancel = useMutation({ mutationFn: disciplineService.cancelIntervention, onSuccess: () => done("The plan was cancelled.") });
  const start = useMutation({ mutationFn: disciplineService.startInterventionAction, onSuccess: (row) => done(`${row.title} is under way.`) });
  const actions = [consent, activate, review, complete, pause, resume, cancel, start];

  const columns: RegisterColumn<InterventionPlan>[] = [
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          {row.student_class && <span className="block text-xs text-gray-500">{row.student_class}</span>}
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[10rem]",
    },
    { header: "Class", cell: (row) => row.student_class, exportValue: (row) => row.student_class, exportOnly: true },
    { header: "Plan", cell: (row) => row.title, exportValue: (row) => row.title, className: "min-w-[12rem] text-gray-900" },
    { header: "Kind", cell: (row) => labelFrom(PLAN_TYPES, row.plan_type), exportValue: (row) => labelFrom(PLAN_TYPES, row.plan_type), className: "text-gray-700" },
    { header: "Runs", cell: (row) => `${day(row.start_date)} – ${day(row.end_date)}`, exportValue: (row) => `${day(row.start_date)} to ${day(row.end_date)}`, className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Sessions", cell: (row) => `${row.actions.filter((item) => item.status === "completed").length} of ${row.actions.filter((item) => item.status !== "cancelled").length}`, exportValue: (row) => row.actions.filter((item) => item.status === "completed").length, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Next review", cell: (row) => (row.status === "active" ? day(row.next_review_date) || "—" : "—"), exportValue: (row) => day(row.next_review_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...planStatus(row, today)} />, exportValue: (row) => planStatus(row, today).label },
    { header: "Staff", cell: (row) => row.responsible_staff_names.join(", "), exportValue: (row) => row.responsible_staff_names.join(", "), exportOnly: true },
    { header: "Goal", cell: (row) => row.primary_goal, exportValue: (row) => row.primary_goal, exportOnly: true },
  ];

  const plan = viewing !== null ? plans?.find((row) => row.id === viewing) : undefined;
  const finished = plan ? plan.status === "completed" || plan.status === "cancelled" : false;
  const needsConsent = plan ? plan.parent_involvement && !plan.parent_consent : false;
  const canRun = (session: InterventionAction) => isReviewer || session.responsible_staff === me;
  const sessions = plan ? [...plan.actions].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date)) : [];

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title="Support plans"
        description="Plans that help a learner change course: sessions, reviews and the outcome. Open one to work on it."
        actions={
          <button type="button" onClick={() => setCreating(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <HeartHandshake className="mr-1.5 h-3.5 w-3.5" /> New support plan
          </button>
        }
        rows={plans}
        config={planConfig(today)}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          actions.forEach((mutation) => mutation.reset());
          setFinishing(false);
          setViewing(row.id);
        }}
        noun={["plan", "plans"]}
        exportName={{ file: "support-plans", title: "Learner Support Plans" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No support plans yet."
      />

      {plan && (
        <DetailsModal
          size="4xl"
          title={plan.title}
          headline={{ label: [plan.student_name, plan.student_class, plan.student_id].filter(Boolean).join(" · "), value: `${day(plan.start_date)} – ${day(plan.end_date)}`, note: `${labelFrom(PLAN_TYPES, plan.plan_type)} plan · reviewed every ${plan.review_frequency_days} days` }}
          status={planStatus(plan, today)}
          notice={needsConsent && plan.status === "draft" ? <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">Record the parent's consent before the plan can start.</p> : undefined}
          facts={[
            ["Made by", `${plan.created_by_name} on ${day(plan.created_at)}`],
            ["Staff responsible", plan.responsible_staff_names.join(", ")],
            ["Parent", plan.parent_involvement ? (plan.parent_consent ? `Agreed ${day(plan.parent_consent_date)}` : "Consent not yet recorded") : "Not involved"],
            ["Last review", day(plan.last_review_date) || undefined],
            ["Next review", plan.status === "active" ? day(plan.next_review_date) : undefined],
            ["Goal met", plan.success_rate != null ? `${Number(plan.success_rate)}%` : undefined],
          ]}
          sections={[
            { title: "Main goal", body: plan.primary_goal },
            { title: "What the support involves", body: plan.description },
            { title: "Objectives", body: plan.specific_objectives.map((line) => `• ${line}`).join("\n") },
            { title: "How success is judged", body: plan.success_metrics.map((line) => `• ${line}`).join("\n") },
            { title: "Why it was paused", body: plan.status === "suspended" ? plan.suspension_reason : "", tone: "warning" },
            { title: "Why it was cancelled", body: plan.cancellation_reason, tone: "danger" },
            { title: "Outcome", body: plan.outcome_summary },
            { title: "Lessons for next time", body: plan.lessons_learned },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {isReviewer && plan.status === "draft" && needsConsent && (
                <button type="button" disabled={consent.isPending} onClick={() => consent.mutate(plan.id)} className="btn btn-outline gap-1.5"><ShieldCheck className="h-4 w-4" /> Parent agreed</button>
              )}
              {isReviewer && plan.status === "draft" && (
                <button type="button" disabled={activate.isPending || needsConsent} onClick={() => activate.mutate(plan.id)} className="btn btn-primary gap-1.5"><Play className="h-4 w-4" /> Start the plan</button>
              )}
              {isReviewer && plan.status === "suspended" && (
                <button type="button" disabled={resume.isPending} onClick={() => resume.mutate(plan.id)} className="btn btn-primary gap-1.5"><Play className="h-4 w-4" /> Resume</button>
              )}
              {isReviewer && plan.status === "active" && !finishing && (
                <button type="button" onClick={() => setFinishing(true)} className="btn btn-primary gap-1.5"><CheckCircle2 className="h-4 w-4" /> Finish the plan</button>
              )}
            </>
          }
        >
          {[consent, activate, resume, start].map((mutation, index) => mutation.isError && <div key={index}>{errorLine(mutation.error, "That could not be done.")}</div>)}

          <section className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sessions ({sessions.length})</h4>
              {!finished && (
                <button type="button" onClick={() => setPlanning(true)} className="btn btn-outline h-8 min-h-8 gap-1 px-2.5 text-xs"><CalendarPlus className="h-3.5 w-3.5" /> Plan a session</button>
              )}
            </div>
            {sessions.length ? (
              <div className="sheet-wrap">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Session</th>
                      <th>Led by</th>
                      <th>Status</th>
                      <th className="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((row) => (
                      <tr key={row.id}>
                        <td className="whitespace-nowrap tabular-nums text-gray-700">{when(row.scheduled_date)}</td>
                        <td className="max-w-[18rem] whitespace-normal">
                          <span className="font-medium text-gray-900">{row.title}</span>
                          <span className="block text-xs text-gray-500">{labelFrom(SESSION_TYPES, row.action_type)} · {row.duration_minutes} min</span>
                          {row.outcome_notes && <span className="mt-1 block text-xs text-gray-700">{row.outcome_notes}{row.effectiveness_rating ? ` (helped: ${row.effectiveness_rating}/5)` : ""}</span>}
                          {(row.cancellation_reason || row.reschedule_reason) && <span className="mt-1 block text-xs text-gray-500">{row.cancellation_reason || row.reschedule_reason}</span>}
                        </td>
                        <td className="text-gray-700">{row.responsible_staff_name}</td>
                        <td><StatusDot {...sessionStatus(row)} /></td>
                        <td className="whitespace-nowrap text-right">
                          {canRun(row) && plan.status === "active" && (row.status === "scheduled" || row.status === "rescheduled") && (
                            <button type="button" disabled={start.isPending} onClick={() => start.mutate(row.id)} className="btn btn-outline h-7 min-h-7 px-2 text-xs">Start</button>
                          )}
                          {canRun(row) && row.status === "in_progress" && (
                            <button type="button" onClick={() => setStep({ session: row, step: "done" })} className="btn btn-primary h-7 min-h-7 px-2 text-xs">Done</button>
                          )}
                          {canRun(row) && (row.status === "scheduled" || row.status === "rescheduled") && (
                            <button type="button" onClick={() => setStep({ session: row, step: "move" })} className="ml-1 rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100">Move</button>
                          )}
                          {canRun(row) && isOpenSession(row) && (
                            <button type="button" onClick={() => setStep({ session: row, step: "cancel" })} className="ml-1 rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50">Cancel</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">No sessions yet. Plan the first, e.g. a mentoring check-in or a parent meeting.</p>
            )}
          </section>

          {plan.reviews.length > 0 && (
            <section className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Reviews ({plan.reviews.length})</h4>
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {plan.reviews.map((row) => (
                  <li key={row.id} className="px-3 py-2 text-sm">
                    <span className="flex flex-wrap justify-between gap-2 text-xs text-gray-500">
                      <span>{day(row.reviewed_at)} · {row.reviewed_by_name}</span>
                      <span>Progress {row.progress_rating} of 5 · {labelFrom(RATINGS, String(row.progress_rating))}</span>
                    </span>
                    <span className="mt-0.5 block whitespace-pre-wrap text-gray-800">{row.progress_notes}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {plan.status === "active" && !finishing && <ReviewPanel key={`review-${plan.id}`} plan={plan} finishing={false} busy={review.isPending} error={review.error} onSubmit={(data) => review.mutate({ id: plan.id, data })} />}
          {isReviewer && plan.status === "active" && finishing && <ReviewPanel key={`finish-${plan.id}`} plan={plan} finishing busy={complete.isPending} error={complete.error} onSubmit={(data) => complete.mutate({ id: plan.id, data })} />}

          {isReviewer && plan.status === "active" && (
            <ReasonPanel
              title="Pause the plan"
              description="Sessions and reviews stop until it is resumed."
              placeholder="e.g. The learner is away for the mid-term break."
              context={{ key: "discipline.note", params: { kind: "suspend", id: plan.id } }}
              button="Pause"
              busy={pause.isPending}
              error={pause.error}
              errorText="The plan could not be paused."
              onSubmit={(reason) => pause.mutate({ id: plan.id, reason })}
            />
          )}
          {isReviewer && !finished && (
            <ReasonPanel
              title="Cancel the plan"
              description="It stays on the learner's record, marked cancelled."
              placeholder="e.g. The learner has transferred to another school."
              context={{ key: "discipline.note", params: { kind: "cancel_plan", id: plan.id } }}
              button="Cancel the plan"
              danger
              busy={cancel.isPending}
              error={cancel.error}
              errorText="The plan could not be cancelled."
              onSubmit={(reason) => cancel.mutate({ id: plan.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {creating && (
        <PlanForm
          learners={learners}
          staff={staff}
          me={me}
          onClose={() => setCreating(false)}
          onDone={(saved) => {
            setCreating(false);
            done(`${saved.title} saved as a draft.`);
            setViewing(saved.id);
          }}
        />
      )}
      {planning && plan && (
        <SessionForm
          plan={plan}
          staff={staff}
          me={me}
          onClose={() => setPlanning(false)}
          onDone={() => {
            setPlanning(false);
            done("The session is planned.");
          }}
        />
      )}
      {step && (
        <SessionStepForm
          step={step}
          onClose={() => setStep(null)}
          onDone={(text) => {
            setStep(null);
            done(text);
          }}
        />
      )}
    </div>
  );
}
